import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import { useAuthStore } from '@/store/useAuthStore';
import { useNavigationStore } from '@/common/store';
import { useTutorialStore } from '@/store/useTutorialStore';
import Avatar from '@/common/components/avatar/Avatar';
import { toastUtils } from '@/common/utils/toastUtils';
import SpaceManagementTutorial from '@/components/tutorial/SpaceManagementTutorial';
import PersonalEmailVerificationModal from '@/components/PersonalEmailVerificationModal';
import {
  Building2,
  Settings,
  Plus,
  Rocket,
  TrendingUp,
  Lightbulb,
  Crown,
  ArrowRight,
  Info,
  UserPlus
} from 'lucide-react';

/**
 * 공간 만들기 메인 페이지
 * 1. 사용자의 회사 목록 표시
 * 2. 선택된 회사의 space 관리
 * 3. 새로운 회사 등록 기능
 */

export type ViewType = 'companyList' | 'company' | 'newCompany';

export interface SelectedCompany {
  id: number;
  name: string;
  code: string;
  service: string[];
}

interface CompanyUser {
  isMaster: boolean;
  company: {
    id: number;
    name: string;
    code: string;
    service: string[];
  };
}

type SpaceType = 'STARTUP' | 'INVESTOR' | 'INNOVATE';

const SpaceManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const { setHeaderTitle, clearBackNavigation, setRestartIntro } = useNavigationStore();
  const { startTutorial, loadFromStorage, resetTutorial } = useTutorialStore();
  
  // 튜토리얼 상태
  const [showTutorial, setShowTutorial] = useState(false);
  
  // 개인 이메일 인증 모달 상태
  const [isPersonalEmailModalOpen, setIsPersonalEmailModalOpen] = useState(false);

  useEffect(() => {
    
    setHeaderTitle('회사 관리');
    clearBackNavigation();
    
    // 인트로 재시작 함수 등록
    setRestartIntro(() => {
      resetTutorial('space-management');
      setTimeout(() => {
        setShowTutorial(true);
      }, 300);
    });
    
    // 튜토리얼 상태 로드
    loadFromStorage();
    
    // URL 파라미터로 튜토리얼 시작 여부 확인
    const shouldStartTutorial = searchParams.get('tutorial') === 'true';
    if (shouldStartTutorial) {
      startTutorial('space-management');
      setShowTutorial(true);
      // URL에서 tutorial 파라미터 제거
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('tutorial');
      window.history.replaceState({}, document.title, newUrl.toString());
    }
    
    // cleanup: 페이지 떠날 때 인트로 재시작 함수 제거
    return () => {
      setRestartIntro(null);
    };
  }, [setHeaderTitle, clearBackNavigation, setRestartIntro, resetTutorial, searchParams, loadFromStorage, startTutorial]);
  
  const handleTutorialComplete = () => {
    setShowTutorial(false);
  };

  // Master 권한이 있는 회사만 필터링
  const masterCompanies = user?.companyUsers.filter(
    (companyUser) => companyUser.isMaster
  );

  /**
   * 서비스 타입별 한글 이름 반환
   */
  const getServiceName = (service: string): string => {
    switch (service) {
      case 'STARTUP':
        return '스타트업';
      case 'INVESTOR':
        return '투자자';
      case 'INNOVATE':
        return '선발 프로그램';
      default:
        return service;
    }
  };

  /**
   * 서비스 타입별 아이콘 반환
   */
  const getServiceIcon = (service: string, size: number = 18) => {
    switch (service) {
      case 'STARTUP':
        return <Rocket size={size} />;
      case 'INVESTOR':
        return <TrendingUp size={size} />;
      case 'INNOVATE':
        return <Lightbulb size={size} />;
      default:
        return <Building2 size={size} />;
    }
  };

  /**
   * 새 회사 등록 버튼 클릭 처리
   */
  const handleNewCompanyClick = (e: React.MouseEvent) => {
    e.preventDefault();

    if (!user?.emailVerified) {
      setIsPersonalEmailModalOpen(true);
      return;
    }

    navigate('/space-management/new-company');
  };

  /**
   * 회사 관리 페이지로 이동
   */
  const handleCompanyManage = (companyCode: string) => {
    if (!user?.emailVerified) {
      setIsPersonalEmailModalOpen(true);
      return;
    }
    navigate(`/space-management/company/${companyCode}`);
  };

  if (!user) {
    return (
      <PageContainer>
        <ContentWrapper>
          <EmptyState>
            <EmptyIconWrapper>
              <Building2 size={48} />
            </EmptyIconWrapper>
            <EmptyTitle>사용자 정보를 불러올 수 없습니다</EmptyTitle>
            <EmptyDescription>다시 로그인해주세요.</EmptyDescription>
          </EmptyState>
        </ContentWrapper>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* 개인 이메일 인증 모달 */}
      <PersonalEmailVerificationModal
        isOpen={isPersonalEmailModalOpen}
        onClose={() => setIsPersonalEmailModalOpen(false)}
        userEmail={user?.email}
      />
      
      {/* 튜토리얼 */}
      {showTutorial && (
        <SpaceManagementTutorial run={showTutorial} onComplete={handleTutorialComplete} />
      )}
      
      <ContentWrapper>
        {/* 히어로 섹션 */}
        <HeroSection data-tutorial="hero-section">
          <HeroBackground>
            <HeroGradient />
            <HeroPattern />
          </HeroBackground>
          <HeroContent>
            <HeroAvatarWrapper>
              <Avatar src={user.profileImage} alt={user.name} isUser={true} sizes="xl" />
            </HeroAvatarWrapper>
            <HeroTextContent>
              <HeroGreeting>회사 관리</HeroGreeting>
              <HeroName>{user.name}님의 관리 회사</HeroName>
              <HeroDescription>
                내가 마스터로 있는 회사만 여기에 표시됩니다.<br/>
                새 회사를 등록하면 마스터가 됩니다.
              </HeroDescription>
            </HeroTextContent>
            <HeroStats>
              <HeroStatItem>
                <HeroStatIcon $type="company">
                  <Building2 size={18} />
                </HeroStatIcon>
                <HeroStatInfo>
                  <HeroStatValue>{masterCompanies?.length || 0}</HeroStatValue>
                  <HeroStatLabel>관리 회사</HeroStatLabel>
                </HeroStatInfo>
              </HeroStatItem>
            </HeroStats>
          </HeroContent>
        </HeroSection>

        {/* 마스터 안내 */}
        <InfoNotice>
          <InfoNoticeIcon>
            <Info size={18} />
          </InfoNoticeIcon>
          <InfoNoticeContent>
            <InfoNoticeTitle>마스터(Master)란?</InfoNoticeTitle>
            <InfoNoticeText>
              회사를 등록하면 자동으로 마스터가 됩니다. 회사 대표가 아니어도 괜찮아요! 
              회사를 셋팅하고 팀원을 초대한 뒤, 다른 사람에게 마스터 권한을 넘겨줄 수도 있습니다.
            </InfoNoticeText>
            <InfoNoticeTips>
              <InfoNoticeTip>
                <UserPlus size={14} />
                <span>팀원이 '회사 찾기'로 가입 신청하면 마스터가 승인할 수 있습니다</span>
              </InfoNoticeTip>
            </InfoNoticeTips>
          </InfoNoticeContent>
        </InfoNotice>

        {/* 회사 목록 */}
        <CompanySection data-tutorial="company-list">
          <SectionHeader>
            <SectionTitleWrapper>
              <SectionIcon>
                <Crown size={20} />
              </SectionIcon>
              <div>
                <SectionTitle>관리 중인 회사</SectionTitle>
                <SectionSubtitle>내가 마스터로 있는 회사만 표시됩니다</SectionSubtitle>
              </div>
            </SectionTitleWrapper>
          </SectionHeader>

          <CompanyGrid>
            {/* 새 회사 등록 카드 */}
            <NewCompanyCard onClick={handleNewCompanyClick} data-tutorial="action-section">
              <NewCompanyIconWrapper>
                <Plus size={28} />
              </NewCompanyIconWrapper>
              <NewCompanyTitle>새 회사 등록</NewCompanyTitle>
              <NewCompanyDescription>
                새 회사를 등록하고 마스터가 되세요
              </NewCompanyDescription>
            </NewCompanyCard>

            {/* 기존 회사 카드들 */}
            {masterCompanies?.map((companyUser, index) => (
              <CompanyCard
                key={companyUser.company.id}
                $delay={index * 0.1}
                $disabled={!user?.emailVerified}
                onClick={() => handleCompanyManage(companyUser.company.code)}
              >
                <CompanyCardHeader>
                  <CompanyAvatarWrapper>
                    <Avatar
                      alt={companyUser.company.name}
                      src={companyUser.company.logo || ''}
                      isUser={false}
                      sizes="lg"
                    />
                  </CompanyAvatarWrapper>
                  <CompanyInfo>
                    <CompanyNameRow>
                      <CompanyName>{companyUser.company.name}</CompanyName>
                      <MasterBadge>
                        <Crown size={12} />
                        마스터
                      </MasterBadge>
                    </CompanyNameRow>
                    <CompanyMeta>
                      <CompanyCode>#{companyUser.company.code}</CompanyCode>
                      <CompanyServiceCount>
                        {companyUser.company.service?.length || 0}개 스페이스
                      </CompanyServiceCount>
                    </CompanyMeta>
                  </CompanyInfo>
                </CompanyCardHeader>

                <ServiceSection>
                  <ServiceTitle>활성화된 스페이스</ServiceTitle>
                  <ServiceList>
                    {companyUser.company.service?.map((service) => (
                      <ServiceBadge key={service} $type={service as SpaceType}>
                        <ServiceBadgeIcon $type={service as SpaceType}>
                          {getServiceIcon(service, 14)}
                        </ServiceBadgeIcon>
                        <ServiceBadgeText>{getServiceName(service)}</ServiceBadgeText>
                      </ServiceBadge>
                    ))}
                    {(!companyUser.company.service ||
                      companyUser.company.service.length === 0) && (
                      <NoServiceText>활성화된 스페이스가 없습니다</NoServiceText>
                    )}
                  </ServiceList>
                </ServiceSection>

                <CardFooter>
                  <ManageButton>
                    <span>관리하기</span>
                    <ArrowRight size={16} />
                  </ManageButton>
                </CardFooter>
              </CompanyCard>
            ))}
          </CompanyGrid>
        </CompanySection>
      </ContentWrapper>
    </PageContainer>
  );
};

// ============================================
// Animations
// ============================================

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// ============================================
// Styled Components
// ============================================

const PageContainer = styled.div`
  min-height: 100%;
  height: 100%;
  padding: 1.5rem;
  background: ${semanticColors.background.primary};
  overflow-y: auto;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
`;

// 히어로 섹션
const HeroSection = styled.div`
  position: relative;
  margin-bottom: 2rem;
  border-radius: 24px;
  overflow: hidden;
  animation: ${fadeInUp} 0.6s ease-out;
`;

const HeroBackground = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #059669 0%, #10b981 50%, #34d399 100%);
`;

const HeroGradient = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 30% 20%, rgba(255,255,255,0.15) 0%, transparent 50%),
              radial-gradient(circle at 70% 80%, rgba(255,255,255,0.1) 0%, transparent 40%);
`;

const HeroPattern = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.1;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
`;

const HeroContent = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 2rem;
  padding: 2.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const HeroAvatarWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const HeroAvatarBadge = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border: 3px solid #059669;
  border-radius: 50%;
  color: #fff;
`;

const HeroTextContent = styled.div`
  flex: 1;
`;

const HeroGreeting = styled.p`
  ${typography.paragraph.p2.default}
  color: rgba(255,255,255,0.95);
  margin-bottom: 0.25rem;
`;

const HeroName = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 0.5rem;
`;

const HeroDescription = styled.p`
  ${typography.paragraph.p2.default}
  color: rgba(255,255,255,0.9);
  max-width: 400px;
`;

const HeroStats = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1rem 1.5rem;
  background: rgba(255,255,255,0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 16px;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const HeroStatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const HeroStatIcon = styled.div<{ $type: 'company' | 'space' }>`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  color: #fff;
  
  ${props => props.$type === 'company' && `
    background: rgba(255, 255, 255, 0.2);
  `}
  
  ${props => props.$type === 'space' && `
    background: rgba(255, 255, 255, 0.2);
  `}
`;

const HeroStatInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeroStatValue = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
  line-height: 1.2;
`;

const HeroStatLabel = styled.span`
  ${typography.caption.caption2}
  color: rgba(255,255,255,0.9);
`;

// 새 회사 등록 카드
const NewCompanyCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 2rem 1.5rem;
  min-height: 220px;
  background: ${semanticColors.background.secondary};
  border: 2px dashed ${semanticColors.border.brand};
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  
  &:hover {
    background: ${semanticColors.background.tertiary};
    border-color: #059669;
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(5, 150, 105, 0.15);
  }
`;

const NewCompanyIconWrapper = styled.div`
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  border-radius: 16px;
  color: #fff;
  transition: transform 0.2s ease;
  
  ${NewCompanyCard}:hover & {
    transform: scale(1.05);
  }
`;

const NewCompanyTitle = styled.h3`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
`;

const NewCompanyDescription = styled.p`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
`;

// 마스터 안내
const InfoNotice = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  background: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-left: 3px solid ${semanticColors.border.brand};
  border-radius: 12px;
  margin-bottom: 2rem;
  animation: ${fadeInUp} 0.6s ease-out 0.1s both;
  
  @media (max-width: 640px) {
    flex-direction: column;
    gap: 0.75rem;
    padding: 1rem 1.25rem;
  }
`;

const InfoNoticeIcon = styled.div`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.tertiary};
  border-radius: 10px;
  color: ${semanticColors.text.brand};
  flex-shrink: 0;
  
  @media (max-width: 640px) {
    width: 32px;
    height: 32px;
  }
`;

const InfoNoticeContent = styled.div`
  flex: 1;
`;

const InfoNoticeTitle = styled.h4`
  ${typography.subtitle.subtitle2}
  color: ${semanticColors.text.primary};
  margin-bottom: 0.375rem;
`;

const InfoNoticeText = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
  line-height: 1.6;
  margin-bottom: 0.75rem;
`;

const InfoNoticeTips = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const InfoNoticeTip = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
  
  svg {
    color: ${semanticColors.text.brand};
    flex-shrink: 0;
  }
`;

// 회사 섹션
const CompanySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  animation: ${fadeInUp} 0.6s ease-out 0.1s both;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SectionTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const SectionIcon = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.warning.secondary};
  border-radius: 10px;
  color: ${semanticColors.text.onWarning.primary};
`;

const SectionTitle = styled.h2`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
`;

const SectionSubtitle = styled.p`
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
  margin-top: 0.125rem;
`;


// 회사 그리드
const CompanyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.25rem;
`;

const CompanyCard = styled.div<{ $delay: number; $disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  background: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 20px;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  animation: ${fadeIn} 0.4s ease-out ${props => props.$delay}s both;
  opacity: ${props => props.$disabled ? 0.6 : 1};
  
  &:hover {
    ${props => !props.$disabled && `
      border-color: ${semanticColors.border.secondary};
      box-shadow: 0 8px 24px rgba(0,0,0,0.06);
      transform: translateY(-4px);
    `}
  }
`;

const CompanyCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.25rem;
`;

const CompanyAvatarWrapper = styled.div`
  flex-shrink: 0;
`;

const CompanyInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const CompanyNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
`;

const CompanyName = styled.h3`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MasterBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  background: ${semanticColors.background.warning.secondary};
  border-radius: 6px;
  ${typography.caption.caption2}
  color: ${semanticColors.text.onWarning.primary};
  font-weight: 600;
`;

const CompanyMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const CompanyCode = styled.span`
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
  font-family: 'Monaco', 'Consolas', monospace;
  background: ${semanticColors.background.primary};
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
`;

const CompanyServiceCount = styled.span`
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
`;

// 서비스 섹션
const ServiceSection = styled.div`
  flex: 1;
  padding-top: 1rem;
  border-top: 1px solid ${semanticColors.border.primary};
`;

const ServiceTitle = styled.h4`
  ${typography.caption.caption1.medium}
  color: ${semanticColors.text.secondary};
  margin-bottom: 0.75rem;
`;

const ServiceList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const ServiceBadge = styled.div<{ $type: SpaceType }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.875rem;
  border-radius: 10px;
  transition: all 0.2s ease;
  
  ${props => props.$type === 'STARTUP' && `
    background: ${semanticColors.background.tertiary};
    border: 1px solid ${semanticColors.border.brand};
  `}
  
  ${props => props.$type === 'INVESTOR' && `
    background: ${semanticColors.background.success.secondary};
    border: 1px solid ${semanticColors.border.success};
  `}
  
  ${props => props.$type === 'INNOVATE' && `
    background: ${semanticColors.background.warning.secondary};
    border: 1px solid ${semanticColors.border.warning};
  `}
`;

const ServiceBadgeIcon = styled.span<{ $type: SpaceType }>`
  display: flex;
  align-items: center;
  
  ${props => props.$type === 'STARTUP' && `color: ${semanticColors.text.brand};`}
  ${props => props.$type === 'INVESTOR' && `color: ${semanticColors.text.onSuccess.primary};`}
  ${props => props.$type === 'INNOVATE' && `color: ${semanticColors.text.onWarning.primary};`}
`;

const ServiceBadgeText = styled.span`
  ${typography.caption.caption1.medium}
  color: ${semanticColors.text.primary};
`;

const NoServiceText = styled.span`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.disabled};
  font-style: italic;
`;

const CardFooter = styled.div`
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid ${semanticColors.border.primary};
`;

const ManageButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: ${semanticColors.background.primary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 10px;
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
  transition: all 0.2s ease;
  
  ${CompanyCard}:hover & {
    background: ${semanticColors.background.tertiary};
    border-color: ${semanticColors.border.brand};
    color: ${semanticColors.text.brand};
  }
`;

// 빈 상태
const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  text-align: center;
  padding: 2rem;
`;

const EmptyIconWrapper = styled.div`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.primary};
  border: 2px dashed ${semanticColors.border.primary};
  border-radius: 20px;
  color: ${semanticColors.text.disabled};
  margin-bottom: 1.5rem;
`;

const EmptyTitle = styled.h3`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
  margin-bottom: 0.5rem;
`;

const EmptyDescription = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.tertiary};
  line-height: 1.6;
`;

export default SpaceManagementPage;
